/**
 * ConstructionBonusTableComponent — 現場賞与 payslip preview (no 労務費 calendar).
 * Layout mirrors construction salary: 振込 → 支給 → 控除 → その他 (with 差引支給額 in last cell).
 */

import React from 'react';
import '../../css/ConstructionTableComponent.css';

function employeeNameDisplayFontSize(displayWithSama: string): string | undefined {
  const len = displayWithSama.length;
  if (len > 14) return '8px';
  if (len > 10) return '9px';
  if (len > 7) return '10px';
  return undefined;
}

interface ConstructionBonusTableComponentProps {
  selectedYear: string;
  selectedPeriod: string;
  empName: string;
  selectedSite: string;
  hasSearched: boolean;
  constructionData: any;
  payslipSettings: {
    borderColor: string;
    backgroundColor: string;
    fontSize: string;
    fontFamily: string;
    showCompanyName: boolean;
    showEmployeeCode: boolean;
    showMemo: boolean;
    furikomi: { enabled: string[] };
    payment: { enabled: string[] };
    deduction: { enabled: string[] };
    notes: { enabled: string[] };
    sonota?: { enabled: string[] };
  };
  furikomiItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  paymentItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  deductionItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  notesItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  sonotaItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
}

const toNumericValue = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const normalized = String(value).replace(/,/g, '').trim();
  if (normalized === '') return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const formatNumber = (value: number, useGrouping: boolean): string => {
  if (useGrouping) {
    return value.toLocaleString('ja-JP');
  }
  return String(value);
};

export const ConstructionBonusTableComponent: React.FC<ConstructionBonusTableComponentProps> = ({
  selectedYear,
  selectedPeriod,
  empName,
  selectedSite,
  constructionData,
  payslipSettings,
  furikomiItems,
  paymentItems,
  deductionItems,
  notesItems,
  sonotaItems,
}) => {
  const title = '賞与明細書';

  React.useEffect(() => {
    document.documentElement.style.setProperty('--payslip-border-color', payslipSettings.borderColor);
    document.documentElement.style.setProperty('--payslip-background-color', payslipSettings.backgroundColor);
    document.documentElement.style.setProperty('--payslip-font-size', payslipSettings.fontSize);
    document.documentElement.style.setProperty('--payslip-font-family', payslipSettings.fontFamily);
  }, [payslipSettings]);

  const enabledFurikomiItems = furikomiItems.filter((item) =>
    payslipSettings.furikomi.enabled.includes(item.id)
  );
  const enabledPaymentItems = paymentItems.filter((item) =>
    payslipSettings.payment.enabled.includes(item.id)
  );
  const enabledDeductionItems = deductionItems.filter((item) =>
    payslipSettings.deduction.enabled.includes(item.id)
  );
  const enabledSonotaItems = sonotaItems.filter((item) =>
    payslipSettings.sonota?.enabled ? payslipSettings.sonota.enabled.includes(item.id) : true
  );
  const enabledNotesItems = notesItems.filter((item) =>
    payslipSettings.notes.enabled.includes(item.id)
  );

  const fieldMapping: Record<string, string> = {
    cb_f_k1: 'transferAmount',
    cb_f_v1: 'transferAmount',
    cb_p_k1: 'bonus',
    cb_d_k1: 'healthInsurance',
    cb_d_k2: 'employmentInsurance',
    cb_d_k3: 'pensionInsurance',
    cb_d_k4: 'incomeTax',
    cb_s_k1: 'otherPayment',
    cb_s_k2: 'otherDeduction',
    totalPayment: 'totalPayment',
    deductionTotal: 'deductionTotal',
    netPaymentAmount: 'netPaymentAmount',
    otherPaymentBreakdown: 'otherPaymentBreakdown',
    otherDeductionBreakdown: 'otherDeductionBreakdown',
    remarks: 'remarks',
  };

  const getDataValue = (keyId: string, defaultValue = '', hideZero = true) => {
    if (!constructionData) return defaultValue;
    const fieldName = fieldMapping[keyId] || keyId;
    const value = constructionData[fieldName];
    const numericValue = toNumericValue(value);
    if (numericValue !== null) {
      if (hideZero && numericValue === 0) return '\u00A0';
      return formatNumber(numericValue, true);
    }
    if (value === null || value === undefined) {
      return defaultValue === '' ? '\u00A0' : defaultValue;
    }
    const stringValue = String(value);
    return stringValue === '' ? '\u00A0' : stringValue;
  };

  const renderKeyValueSection = (
    enabledItems: typeof paymentItems,
    sectionName: string,
    className: string
  ) => {
    if (enabledItems.length === 0) return null;
    return (
      <>
        <tr className={`${className} green-row`}>
          <td
            rowSpan={2}
            className="construction-vertical-text construction-thick-border-left construction-thick-border-top construction-thick-border-bottom construction-thick-border-right"
          >
            {sectionName}
          </td>
          {enabledItems.map((item, index) => (
            <td
              key={item.keyId}
              className={`construction-thick-border-top construction-border-left construction-no-bottom-border ${
                index === 5 ? 'construction-thick-border-right' : ''
              }`}
            >
              {item.name}
            </td>
          ))}
          {Array.from({ length: 6 - enabledItems.length }, (_, index) => (
            <td
              key={`empty-${sectionName}-key-${index}`}
              className={`construction-thick-border-top construction-border-left construction-no-bottom-border ${
                enabledItems.length + index === 5 ? 'construction-thick-border-right' : ''
              }`}
            >
              {'\u00A0'}
            </td>
          ))}
        </tr>
        <tr className={className}>
          {enabledItems.map((item, index) => (
            <td
              key={item.valueId}
              className={`construction-border-left construction-thick-border-bottom construction-border-top ${
                index === 5 ? 'construction-thick-border-right' : ''
              }`}
            >
              {getDataValue(item.keyId, '')}
            </td>
          ))}
          {Array.from({ length: 6 - enabledItems.length }, (_, index) => (
            <td
              key={`empty-${sectionName}-value-${index}`}
              className={`construction-border-left construction-thick-border-bottom construction-border-top ${
                enabledItems.length + index === 5 ? 'construction-thick-border-right' : ''
              }`}
            >
              {'\u00A0'}
            </td>
          ))}
        </tr>
      </>
    );
  };

  /** Labels left (up to 5), trailing total in last cell — same pattern as salary 支給/控除. */
  const renderSectionWithTrailingTotal = (
    enabledItems: typeof paymentItems,
    sectionName: string,
    className: string,
    totalLabel: string,
    totalValueKey: string
  ) => {
    const rowItems = enabledItems.slice(0, 5);
    const fillCount = Math.max(0, 5 - rowItems.length);
    return (
      <>
        <tr className={`${className} green-row`}>
          <td
            rowSpan={2}
            className="construction-vertical-text construction-thick-border-left construction-thick-border-top construction-thick-border-bottom construction-thick-border-right"
          >
            {sectionName}
          </td>
          {rowItems.map((item) => (
            <td
              key={item.keyId}
              className="construction-thick-border-top construction-border-left construction-no-bottom-border"
            >
              {item.name}
            </td>
          ))}
          {Array.from({ length: fillCount }, (_, index) => (
            <td
              key={`empty-${sectionName}-key-${index}`}
              className="construction-thick-border-top construction-border-left construction-no-bottom-border"
            >
              {'\u00A0'}
            </td>
          ))}
          <td className="construction-thick-border-top construction-border-left construction-no-bottom-border construction-thick-border-right">
            {totalLabel}
          </td>
        </tr>
        <tr className={className}>
          {rowItems.map((item) => (
            <td
              key={item.valueId}
              className="construction-border-left construction-thick-border-bottom construction-border-top"
            >
              {getDataValue(item.keyId, '')}
            </td>
          ))}
          {Array.from({ length: fillCount }, (_, index) => (
            <td
              key={`empty-${sectionName}-value-${index}`}
              className="construction-border-left construction-thick-border-bottom construction-border-top"
            >
              {'\u00A0'}
            </td>
          ))}
          <td className="construction-border-left construction-thick-border-bottom construction-border-top construction-thick-border-right">
            {getDataValue(totalValueKey, '')}
          </td>
        </tr>
      </>
    );
  };

  /** Same layout as salary renderSonotaSection: labels left, 差引支給額 fixed last column. */
  const renderSonotaSection = () => {
    const fillCount = 5 - enabledSonotaItems.length;
    return (
      <>
        <tr className="construction-sonota-section green-row">
          <td
            rowSpan={2}
            className="construction-vertical-text construction-thick-border-left construction-thick-border-top construction-thick-border-bottom construction-thick-border-right"
          >
            その他
          </td>
          {enabledSonotaItems.map((item) => (
            <td
              key={`sonota-key-${item.keyId}`}
              className="construction-thick-border-top construction-border-left construction-no-bottom-border"
            >
              {item.name}
            </td>
          ))}
          {Array.from({ length: Math.max(0, fillCount) }, (_, index) => (
            <td
              key={`sonota-empty-${index}`}
              className="construction-thick-border-top construction-border-left construction-no-bottom-border"
            >
              {'\u00A0'}
            </td>
          ))}
          <td className="construction-thick-border-top construction-border-left construction-no-bottom-border construction-thick-border-right">
            差引支給額
          </td>
        </tr>
        <tr className="construction-sonota-section">
          {enabledSonotaItems.map((item) => (
            <td
              key={`sonota-value-${item.valueId}`}
              className="construction-border-left construction-thick-border-bottom construction-border-top"
            >
              {getDataValue(item.keyId, '')}
            </td>
          ))}
          {Array.from({ length: Math.max(0, fillCount) }, (_, index) => (
            <td
              key={`sonota-empty-value-${index}`}
              className="construction-border-left construction-thick-border-bottom construction-border-top"
            >
              {'\u00A0'}
            </td>
          ))}
          <td className="construction-border-left construction-thick-border-bottom construction-border-top construction-thick-border-right">
            {getDataValue('netPaymentAmount', '')}
          </td>
        </tr>
      </>
    );
  };

  const displayNameText = (() => {
    const n = (constructionData?.employeeName || empName || '').trim();
    return n ? `${n}様` : '様';
  })();
  const displayNameFontSize = employeeNameDisplayFontSize(displayNameText);
  // Match salary slip: genba on first line, year/month below (same as head-office 年月分 format).
  const periodLabel = selectedPeriod.endsWith('月')
    ? `${selectedYear}年${selectedPeriod}分`
    : `${selectedYear}年${selectedPeriod}月分`;

  return (
    <div className="construction-payslip-container">
      <table
        className="construction-main-table"
        style={{
          fontFamily: payslipSettings.fontFamily,
          fontSize: payslipSettings.fontSize,
        }}
      >
        <thead>
          <tr>
            <td colSpan={2} className="construction-border-rect">
              組織コード
            </td>
            {payslipSettings.showEmployeeCode && <td className="construction-border-rect">社員コード</td>}
            <td className="construction-border-rect">氏 名</td>
            <td
              colSpan={payslipSettings.showEmployeeCode ? 2 : 3}
              className="construction-no-border construction-title-cell"
            >
              <span className="construction-title">{title}</span>
            </td>
          </tr>
          <tr>
            <td colSpan={2} className="construction-border-rect" />
            {payslipSettings.showEmployeeCode && <td className="construction-border-rect" />}
            <td className="construction-border-rect construction-employee-name-cell">
              <span
                className="construction-employee-name-text"
                style={displayNameFontSize ? { fontSize: displayNameFontSize } : undefined}
              >
                {displayNameText}
              </span>
            </td>
            <td
              colSpan={payslipSettings.showEmployeeCode ? 2 : 3}
              className="construction-no-border construction-sub-title-cell"
            >
              <span className="construction-sub-title">
                {selectedSite}
                <br />
                {periodLabel}
              </span>
            </td>
          </tr>
        </thead>
        <tbody>
          <tr className="construction-honsha-row">
            <td colSpan={7} style={{ textAlign: 'right' }}></td>
          </tr>

          {renderKeyValueSection(enabledFurikomiItems, '振込', 'construction-furikomi-section')}

          {enabledFurikomiItems.length > 0 && (
            <tr className="construction-separator-row">
              <td colSpan={7} className="construction-white-bg construction-no-border" />
            </tr>
          )}

          {renderSectionWithTrailingTotal(
            enabledPaymentItems,
            '支給',
            'construction-shikyu-section',
            '支給合計',
            'totalPayment'
          )}

          <tr className="construction-separator-row">
            <td colSpan={7} className="construction-white-bg construction-no-border" />
          </tr>

          {renderSectionWithTrailingTotal(
            enabledDeductionItems,
            '控除',
            'construction-koujo-section',
            '控除合計',
            'deductionTotal'
          )}

          <tr className="construction-separator-row">
            <td colSpan={7} className="construction-white-bg construction-no-border" />
          </tr>

          {renderSonotaSection()}
        </tbody>
      </table>

      {payslipSettings.showMemo && (
        <table className="construction-main-table construction-memo-between-table" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '18%' }} />
            <col style={{ width: '82%' }} />
          </colgroup>
          <tbody>
            {enabledNotesItems.length > 0 ? (
              enabledNotesItems.map((item, index) => {
                const isFirstRow = index === 0;
                const isLastRow = index === enabledNotesItems.length - 1;
                return (
                  <tr
                    key={item.id}
                    className="construction-no-name-section construction-memo-section construction-memo-between-section"
                  >
                    <td
                      className={`construction-thick-border-left construction-no-right-border construction-no-name-section-key construction-memo-label-cell ${
                        isFirstRow ? 'construction-thick-border-top construction-border-bottom' : 'construction-no-top-border'
                      } ${isLastRow ? 'construction-thick-border-bottom' : 'construction-border-bottom'}`}
                    >
                      {item.name}
                    </td>
                    <td
                      className={`construction-border-left construction-thick-border-right construction-no-name-section-value construction-memo-value-cell ${
                        isFirstRow ? 'construction-thick-border-top construction-border-bottom' : 'construction-no-top-border'
                      } ${isLastRow ? 'construction-thick-border-bottom' : 'construction-border-bottom'}`}
                    >
                      {getDataValue(item.keyId, '', false)}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr className="construction-no-name-section construction-memo-section construction-memo-between-section">
                <td
                  colSpan={2}
                  className="construction-thick-border-left construction-thick-border-right construction-thick-border-top construction-thick-border-bottom construction-memo-label-cell"
                >
                  {'\u00A0'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {payslipSettings.showCompanyName && (
        <table className="construction-main-table construction-company-memo-table">
          <tbody>
            <tr className="construction-separator-row">
              <td
                colSpan={7}
                className="construction-white-bg construction-no-border"
                style={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: `calc(${payslipSettings.fontSize} * 1.6)`,
                }}
              >
                株式会社西行土木
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
};
